import { type ReactElement, useEffect, useState } from 'react'
import TokenRangeInput, { type TokenRangeInputData } from './tokenRangeInput'
import TokenListInput, { type TokenListInputData } from './tokenListInput'
import ContractFullInput, {
	type ContractFullInputData,
} from './contractFullInput'
import { DynamicContext } from '@dynamic-labs/sdk-react-core'
import { Modal } from '../../modalsAndOverlays'
import { TypographyLabel, TypographyParagraph } from '../../typography'
import RadioInput from '../radioInput'
import { Button } from '../../buttons'
import { type MetadataProperties } from '#app/server/db.server'
import { CollectionNFTInputType } from '#app/utils/templates/utils/templateTypes.js'
import {
	getArrayDuplicates,
	isNumeric,
	isValidSolanaAddress,
	validateContract,
} from '#app/utils/misc.js'

export const collectionInputOptions = [
	{
		name: 'Contract Token List',
		slug: 'smartContractIDs',
		buttonText: 'Token list',
		icon: null,
		component: TokenListInput,
		version: '1.0.0',
	},
	{
		name: 'Contract Token Range',
		slug: 'smartContractRange',
		buttonText: 'Token range',
		icon: null,
		component: TokenRangeInput,
		version: '1.0.0',
	},
	{
		name: 'All Tokens In Contract',
		slug: 'smartContractFull',
		buttonText: 'All',
		icon: null,
		component: ContractFullInput,
		version: '1.0.0',
	},
]

export type CollectionInputDataTypes =
	| TokenRangeInputData
	| TokenListInputData
	| ContractFullInputData

export type InputOption = {
	id?: number
	name: string
	slug: string
	icon: any
	component: ReactElement
}

export type InvalidatedNFTInputFields = {
	chain?: string
	contract?: string
	tokenIDs?: string
	tokenStart?: string
	tokenEnd?: string
}

export const CollectionInputModal = ({
	isOpen,
	onClose,
	formData,
	existingInput,
	handleInputDataChange,
	errorFields,
}: {
	isOpen: boolean
	onClose: () => void
	formData: MetadataProperties | {}
	existingInput?: CollectionNFTInputType
	errorFields: { [key: string]: { [key: string]: string } }
	handleInputDataChange: (val: CollectionNFTInputType) => void
}) => {
	const [inputInvalidatedFields, setInputInvalidatedFields] =
		useState<InvalidatedNFTInputFields>({})
	const getInputId = () => {
		try {
			if (!existingInput) {
				if (formData && formData.nfts && formData.nfts.length > 0) {
					const formInputs = formData.nfts.map(
						(entry: CollectionInputDataTypes) => {
							const input = collectionInputOptions.find(
								inputOption => entry.type === inputOption.slug,
							)
							return { ...input, id: Number(entry.inputSelector.split('-')[1]) }
						},
					)
					const highestID = Math.max(
						formInputs
							.filter(entry => entry.id && !isNaN(entry.id))
							.map(entry => entry.id),
					)
					if (!isNaN(highestID)) {
						return highestID + 1
					} else if (formInputs.length) {
						return formInputs.length + 1
					} else {
						return 0
					}
				} else {
					return 0
				}
			} else {
				const existingInputSelector =
					existingInput && existingInput.inputSelector
						? existingInput.inputSelector
						: undefined
				const inputSelectors = existingInputSelector
					? existingInputSelector.split('-')
					: undefined
				const existingInputID =
					inputSelectors && inputSelectors[1] ? inputSelectors[1] : undefined
				if (existingInputID) {
					return existingInputID
				} else if (formData && formData.nfts && formData.nfts.length) {
					return formData.nfts.length + 1
				} else {
					return 0
				}
			}
		} catch (e) {
			console.error('ERROR GETTING INPUT ID', e)
			if (formData && formData.nfts && formData.nfts.length > 0) {
				return formData.nfts.length + 1
			} else {
				return 0
			}
		}
	}

	const [currentInputType, setCurrentInputType] = useState<string>(
		existingInput ? existingInput.type : collectionInputOptions[0].slug,
	)
	const [inputValues, setInputValues] = useState<
		CollectionNFTInputType | undefined
	>(existingInput ? existingInput : undefined)
	const [error, setError] = useState<{ [key: string]: string } | undefined>(
		undefined,
	)
	const [id, setId] = useState<number>(getInputId())

	useEffect(() => {
		if (isOpen) {
			const iid = getInputId()
			setId(iid)
		}
	}, [isOpen])

	const handleChange = (val: CollectionInputDataTypes, key?: string) => {
		validateNFTEntry(val, key)
		setInputValues(val)
	}

	const handleSubmit = () => {
		if (
			inputValues &&
			inputValues.chain &&
			(inputValues.contract || inputValues.chain === 'solana')
		) {
			// @ts-ignore
			const invalidatedNFTInputFields = validateNFTEntry(inputValues)
			if (
				invalidatedNFTInputFields &&
				Object.keys(invalidatedNFTInputFields).length > 0
			) {
				return
			}
			if (
				inputValues.type === 'smartContractIDs' &&
				inputValues.tokenIDs &&
				inputValues.tokenIDs.length > 0
			) {
				handleInputDataChange(inputValues)
				onClose()
			} else {
				setInputInvalidatedFields({ tokenIDs: 'Please enter' })
				setError({ tokenIDs: 'Please enter more than one tokenID' })
			}
			if (inputValues.type === 'smartContractRange') {
				handleInputDataChange(inputValues)
				onClose()
			} else {
				setError({ contractRange: 'Please enter valid tokenID' })
			}
			if (inputValues.type === 'smartContractFull') {
				handleInputDataChange(inputValues)
				onClose()
			} else {
				onClose()
			}
		}
	}

	const validateNFTEntry = (
		currentNFTInput: CollectionInputDataTypes,
		key?: string,
	) => {
		if (currentNFTInput) {
			const invalidatedFields: InvalidatedNFTInputFields = {}
			if (currentNFTInput.type === 'smartContractRange') {
				if (currentNFTInput.chain === '') {
					invalidatedFields.chain = 'Please select a Chain'
				} else {
					if (invalidatedFields.chain) {
						delete invalidatedFields.chain
					}
				}
				if (key === undefined || key === 'contract') {
					const contractValidationResult = validateContract(
						currentNFTInput.chain,
						currentNFTInput.contract,
					)
					if (contractValidationResult.isValid === true) {
						invalidatedFields.contract && delete invalidatedFields.contract
					} else {
						invalidatedFields.contract = contractValidationResult.message
					}
				}

				if (key === undefined || key === 'tokenStart' || key === 'tokenEnd') {
					if (currentNFTInput.tokenStart === '') {
						invalidatedFields.tokenStart =
							'Please enter a valid starting token.'
					} else {
						invalidatedFields.tokenStart && delete invalidatedFields.tokenStart
					}
					if (currentNFTInput.tokenEnd === '') {
						invalidatedFields.tokenEnd = 'Please enter a valid ending token.'
					} else {
						invalidatedFields.tokenEnd && delete invalidatedFields.tokenEnd
					}

					if (
						currentNFTInput.tokenStart !== '' &&
						currentNFTInput.tokenEnd !== ''
					) {
						if (
							Number(currentNFTInput.tokenStart) >
							Number(currentNFTInput.tokenEnd)
						) {
							invalidatedFields.tokenEnd =
								'Make sure Token End is After Token Start.'
						} else {
							invalidatedFields.tokenStart &&
								delete invalidatedFields.tokenStart
							invalidatedFields.tokenEnd && delete invalidatedFields.tokenEnd
						}
					}
				}
				// @ts-ignore
				// setErrorFields()
			} else if (currentNFTInput.type === 'smartContractIDs') {
				if (currentNFTInput.chain === '') {
					invalidatedFields.chain = 'Please select a Chain.'
				} else {
					if (invalidatedFields.chain) {
						delete invalidatedFields.chain
					}
				}
				if (key === undefined || key === 'tokenID') {
					if (currentNFTInput.tokenIDs.length === 0) {
						invalidatedFields.tokenIDs = 'Please enter at least one token ID.'
					} else if (currentNFTInput.tokenIDs.length > 0) {
						if (currentNFTInput.chain === 'solana') {
							const invalidSolanaTokenIDs = currentNFTInput.tokenIDs.filter(
								tokenID => !isValidSolanaAddress(tokenID),
							)

							if (invalidSolanaTokenIDs.length > 0) {
								invalidatedFields.tokenIDs = `Please ensure all Token Addresses are valid. found the following invalid Token Addresses: ${invalidSolanaTokenIDs.toString()}`
							} else {
								const tokenIDsSet = new Set(currentNFTInput.tokenIDs)
								if (tokenIDsSet.size !== currentNFTInput.tokenIDs.length) {
									const arrayDuplicates = getArrayDuplicates(
										currentNFTInput.tokenIDs,
									)
									const arrayDuplicateSet = new Set(arrayDuplicates)
									invalidatedFields.tokenIDs = `Please ensure all Token Addresses are unique, found duplicated Addresses: ${Array.from(arrayDuplicateSet).join(', ')}`
								} else {
									if (invalidatedFields.tokenIDs)
										delete invalidatedFields.tokenIDs
								}
							}
						} else {
							const nonNumericTokenIDs = currentNFTInput.tokenIDs.filter(
								tokenID => !isNumeric(tokenID),
							)

							if (nonNumericTokenIDs.length > 0) {
								invalidatedFields.tokenIDs = `Please ensure all tokenIDs are all Numbers. found: ${nonNumericTokenIDs.toString()}`
							} else {
								const tokenIDsSet = new Set(currentNFTInput.tokenIDs)
								if (tokenIDsSet.size !== currentNFTInput.tokenIDs.length) {
									const arrayDuplicates = getArrayDuplicates(
										currentNFTInput.tokenIDs,
									)
									const arrayDuplicateSet = new Set(arrayDuplicates)
									invalidatedFields.tokenIDs = `Please ensure all tokenIDs are unique, found duplicated IDs: ${Array.from(arrayDuplicateSet).join(', ')}`
								} else {
									if (invalidatedFields.tokenIDs)
										delete invalidatedFields.tokenIDs
								}
							}
						}
					}
				}
				if (key === undefined || key === 'contract') {
					const contractValidationResult = validateContract(
						currentNFTInput.chain,
						currentNFTInput.contract,
					)
					if (contractValidationResult.isValid === true) {
						invalidatedFields.contract && delete invalidatedFields.contract
					} else {
						invalidatedFields.contract = contractValidationResult.message
					}
				}
			} else if (currentNFTInput.type === 'smartContractFull') {
				if (currentNFTInput.chain === '') {
					invalidatedFields.chain = 'Please select a Chain'
				} else {
					if (invalidatedFields.chain) {
						delete invalidatedFields.chain
					}
				}
				if (key === undefined || key === 'contract') {
					const contractValidationResult = validateContract(
						currentNFTInput.chain,
						currentNFTInput.contract,
					)
					if (contractValidationResult.isValid === true) {
						invalidatedFields.contract && delete invalidatedFields.contract
					} else {
						invalidatedFields.contract = contractValidationResult.message
					}
				}
			}
			setInputInvalidatedFields(invalidatedFields)
			return invalidatedFields
		}
	}

	return (
		<Modal
			size="large"
			title="Add NFTs Manually"
			body={
				!DynamicContext._currentValue ? (
					<div className="max-w-[560px]">...Loading</div>
				) : (
					<div className="max-w-[560px]">
						<TypographyParagraph size="medium">
							Add NFTs manually by inputting token details for a single token,
							token range, or all tokens from a contract address.
						</TypographyParagraph>
						<div className="mb-2.5 mt-6 flex items-center justify-between">
							<TypographyLabel size="medium" isBold={true}>
								Import Type
							</TypographyLabel>
							<div className="my-auto flex justify-between">
								{collectionInputOptions.map(input => {
									return (
										<div key={input.slug} className="flex items-center">
											<RadioInput
												size="large"
												name={input.buttonText}
												current={
													currentInputType !== undefined
														? currentInputType === input.slug
														: collectionInputOptions[0].slug === input.slug
												}
												onClick={() => {
													setCurrentInputType(input.slug)
												}}
											/>
										</div>
									)
								})}
							</div>
						</div>
						<div>
							{currentInputType &&
								collectionInputOptions.map(input => {
									if (input.slug === currentInputType) {
										return (
											<div key={input.slug}>
												<input.component
													inputData={existingInput ? existingInput : undefined}
													errorFields={inputInvalidatedFields}
													onChange={handleChange}
													id={String(id)}
													slug={input.slug}
												/>
											</div>
										)
									} else {
										return null
									}
								})}
							<Button
								additionalClasses={'mt-8 w-full'}
								variant="primary"
								size="large"
								disabled={
									inputValues === undefined ||
									Object.keys(inputInvalidatedFields).length !== 0
								}
								onClick={() => {
									handleSubmit()
								}}
							>
								{existingInput ? 'Update NFTs' : 'Add NFTs'}
							</Button>
						</div>
					</div>
				)
			}
			isOpen={isOpen}
			onClose={() => onClose()}
			showClose={true}
		/>
	)
}
